import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PageLayout from 'components/layouts/PageLayout';
import Modal from 'components/Modal';
import ReactSignatureCanvas from 'react-signature-canvas';
import { formatPhoneNumber, validatePhone } from 'utils/validatePhone';
import { validateZip, formatZipCode } from 'utils/validateZip';
import setLocalStorage from 'utils/setLocalStorage';
import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID, postWP } from 'services/wp';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import LumpSumApplicationDoc from './components/LumpSumApplicationDoc';
import {
  COVERAGE_TIERS,
  calculateLumpSumRate,
  calculateAgeFromDOB,
  getDOBLimits,
  generateReferenceId
} from './data/rateData';
import {
  FaCheckCircle,
  FaArrowRight,
  FaArrowLeft,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaRedo,
  FaFileSignature,
  FaLock,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaInfoCircle,
  FaTimes,
  FaArrowCircleUp
} from 'react-icons/fa';
import { CiEraser } from 'react-icons/ci';
import ls1 from 'images/lump_sum/ls1.png';
import ls2 from 'images/lump_sum/ls2.png';
import ls3 from 'images/lump_sum/ls3.png';
import ls4 from 'images/lump_sum/ls4.png';
import TermsAndConditionContent from '../terms_and_condition/content/TermsAndConditionContent';
import PrivacyPolicyContent from '../privacy_policy/content/PrivacyPolicyContent';

const TCPA_CALCULATOR_DISCLAIMER =
  "I agree to the Terms and Conditions, Privacy Policy, and Communication Agreement. By checking this box and clicking 'Continue to Pre-Approval', I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. Consent is not a condition of purchase.";

const ACH_DISCLAIMER_TEXT =
  "By checking this box, typing/drawing my signature above, and clicking 'Authorize and Submit Application', I agree to the Insurance Enterprise Terms and Conditions and Electronic Agreement. I authorize Insurance Enterprise and its underwriting partners to initiate recurring monthly ACH direct debits from the bank account provided for my policy premium upon formal approval. I confirm that all banking, health, and eligibility statements provided in this application are accurate to the best of my knowledge.";

const MONTHS = [
  { value: '01', name: 'January' },
  { value: '02', name: 'February' },
  { value: '03', name: 'March' },
  { value: '04', name: 'April' },
  { value: '05', name: 'May' },
  { value: '06', name: 'June' },
  { value: '07', name: 'July' },
  { value: '08', name: 'August' },
  { value: '09', name: 'September' },
  { value: '10', name: 'October' },
  { value: '11', name: 'November' },
  { value: '12', name: 'December' }
];

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

const formatDateTime = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const LumpSumBenefits = () => {
  useEffect(() => {
    document.title = "Cancer, Heart Attack, Stroke Lump Sum Plan - Insurance Enterprise";
    window.scrollTo(0, 0);
  }, []);

  // Multi-step Carousel:
  // 1 = Coverage Selection & Demographics
  // 2 = Estimated Rate & Contact Info (First Name, Last Name, Email, Phone, TCPA)
  // 3 = 3 Health Pre-Approval Questions
  // 4 = Direct Deposit (ACH) Payment and Signature Page
  // 5 = Confirmation
  const [currentStep, setCurrentStep] = useState(1);

  // Form Inputs
  const [formData, setFormData] = useState({
    zipCode: '',
    dob: '',
    age: null,
    gender: '',
    coverageAmount: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    tcpaConsent: false
  });

  const [dobParts, setDobParts] = useState({ year: '', month: '', day: '' });

  const [step1Errors, setStep1Errors] = useState({});
  const [step2Errors, setStep2Errors] = useState({});

  // Step 3: Health Questionnaire (3 Cards)
  const [questionnaire, setQuestionnaire] = useState({
    q1_replacing_or_declined: '',
    q1_details: '',
    q2_cancer_or_tests: '',
    q2_details: '',
    q3_hiv_aids: '',
    q3_details: ''
  });

  const [questionnaireErrors, setQuestionnaireErrors] = useState({});

  // Step 4: Bank Account Authorization & Signature
  const [achPayment, setAchPayment] = useState({
    routingNumber: '',
    bankName: '',
    accountNumber: '',
    bankCustomerName: '',
    accountType: 'checking',
    signatureType: 'draw',
    achConsent: true
  });

  const [achErrors, setAchErrors] = useState({});
  const sigCanvasRef = useRef(null);

  // Legal and TCPA Modal (Unified 3-tab popup)
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalTab, setLegalTab] = useState('terms'); // 'terms' | 'privacy' | 'comm'

  const openLegalModal = (tab = 'terms') => {
    setLegalTab(tab);
    setShowLegalModal(true);
  };


  // Submission & Feedback State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasSubmittedLead, setHasSubmittedLead] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  
  // Centralized Toast Notifications
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const toastTimeoutRef = useRef(null);
  const toastBoxRef = useRef(null);

  const hideToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast((prev) => ({ ...prev, show: false }));
  }, []);

  const triggerToast = useCallback((message, type = 'info', duration = 4500) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ show: true, message, type });
    if (type !== 'error' && type !== 'warning') {
      toastTimeoutRef.current = setTimeout(() => {
        setToast((prev) => ({ ...prev, show: false }));
      }, duration);
    }
  }, []);

  useEffect(() => {
    if (!toast.show) return undefined;
    let active = false;
    const timer = setTimeout(() => {
      active = true;
    }, 150);

    const handleOutsideClick = (e) => {
      if (!active) return;
      if (toastBoxRef.current && !toastBoxRef.current.contains(e.target)) {
        hideToast();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [toast.show, hideToast]);

  const [applicationRefId, setApplicationRefId] = useState(() => {
    try {
      const saved = sessionStorage.getItem('ie_lump_sum_ref_id');
      if (saved) return saved;
    } catch (e) {}
    const newId = generateReferenceId();
    try {
      sessionStorage.setItem('ie_lump_sum_ref_id', newId);
    } catch (e) {}
    return newId;
  });
  const applicationDocRef = useRef(null);
  const [docSignatureImage, setDocSignatureImage] = useState('');

  // Real-World Scenarios Tab & Lightbox State
  const [activeScenario, setActiveScenario] = useState('mary'); // 'mary' | 'john'
  const [lightboxImage, setLightboxImage] = useState(null);

  // Handle ESC key and scroll locking when fullscreen lightbox is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxImage(null);
      }
    };
    if (lightboxImage) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage]);

  // Handle URL Parameters for GHL / Email Follow-Up Auto-fill & Step 2 Fast-Track
  useEffect(() => {
    try {
      if (!window.location.search) return;
      const searchParams = new URLSearchParams(window.location.search);
      if (!searchParams || Array.from(searchParams.keys()).length === 0) return;

      // Create case-insensitive, normalized query param map
      const normalizedParams = new Map();
      searchParams.forEach((value, key) => {
        const normalizedKey = key.toLowerCase().replace(/[-_\s]/g, '');
        normalizedParams.set(normalizedKey, value);
      });

      const getParam = (...keys) => {
        for (const k of keys) {
          const normalizedK = k.toLowerCase().replace(/[-_\s]/g, '');
          const val = normalizedParams.get(normalizedK);
          if (val && typeof val === 'string') {
            try {
              return decodeURIComponent(val).trim();
            } catch (e) {
              return val.trim();
            }
          }
        }
        return '';
      };

      const firstName = getParam('firstName', 'first_name', 'fn', 'first', 'fname');
      const lastName = getParam('lastName', 'last_name', 'ln', 'last', 'lname');
      const email = getParam('email', 'em', 'email_address', 'mail');
      const rawPhone = getParam('phone', 'ph', 'cell_phone', 'cellphone', 'mobile', 'tel', 'phone_number', 'telephone');
      const zipCode = getParam('zipCode', 'zip_code', 'zip', 'postal_code', 'postal', 'postcode');
      const dob = getParam('dob', 'dateOfBirth', 'date_of_birth', 'birth_date', 'birthdate', 'birthday', 'bday', 'date');
      const rawGender = getParam('gender', 'gen', 'sex').toLowerCase();
      const rawCoverage = getParam('coverageAmount', 'coverage_amount', 'coverage', 'cov', 'benefit', 'amount', 'plan', 'tier');
      const stepParam = getParam('step', 'currentStep', 'stage');
      const refId = getParam('ref', 'refId', 'referenceId', 'ref_id', 'reference_id');

      const normalizePhone = (raw) => {
        if (!raw) return '';
        let str = String(raw).trim();
        try { str = decodeURIComponent(str); } catch (e) {}
        const digits = str.replace(/\D/g, '');
        const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
        if (tenDigits.length === 10) {
          return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6, 10)}`;
        }
        return formatPhoneNumber(str) || str;
      };

      const phone = normalizePhone(rawPhone);
      const gender = (rawGender.startsWith('f') || rawGender === 'female' || rawGender === 'woman') ? 'female' : (rawGender.startsWith('m') || rawGender === 'male' || rawGender === 'man') ? 'male' : '';
      
      let parsedCoverage = null;
      if (rawCoverage) {
        let cleaned = String(rawCoverage).toLowerCase().replace(/[$,\s'"]/g, '').trim();
        // Strip cents/decimals if GHL outputs currency with decimals (e.g. 110000.00 or $110,000.00)
        if (cleaned.includes('.')) {
          cleaned = cleaned.split('.')[0];
        }
        if (cleaned.endsWith('k')) {
          const num = parseFloat(cleaned.slice(0, -1));
          if (!isNaN(num)) parsedCoverage = Math.round(num * 1000);
        } else {
          const num = parseInt(cleaned.replace(/\D/g, ''), 10);
          if (!isNaN(num)) {
            // Support numbers like 10-150 (thousands) or 10000-150000
            parsedCoverage = (num >= 10 && num <= 150) ? num * 1000 : num;
          }
        }
      }

      let coverageAmount = 50000;
      if (parsedCoverage) {
        const exactMatch = COVERAGE_TIERS.find((t) => t.value === parsedCoverage);
        if (exactMatch) {
          coverageAmount = exactMatch.value;
        } else {
          // Snap to the closest tier
          const closestTier = COVERAGE_TIERS.reduce((prev, curr) =>
            Math.abs(curr.value - parsedCoverage) < Math.abs(prev.value - parsedCoverage) ? curr : prev
          );
          coverageAmount = closestTier ? closestTier.value : 50000;
        }
      }

      // Universal DOB Parser supporting GHL Date Picker ("Jun 15, 1986", "1986-06-15", "06/15/1986", timestamps, etc.)
      const normalizeDOBString = (raw) => {
        if (!raw) return '';
        let str = String(raw).trim();
        try { str = decodeURIComponent(str); } catch (e) {}
        str = str.replace(/\+/g, ' ').replace(/,/g, ' ').replace(/\./g, '-').trim();

        const monthMap = {
          jan: '01', january: '01',
          feb: '02', february: '02',
          mar: '03', march: '03',
          apr: '04', april: '04',
          may: '05',
          jun: '06', june: '06',
          jul: '07', july: '07',
          aug: '08', august: '08',
          sep: '09', september: '09',
          oct: '10', october: '10',
          nov: '11', november: '11',
          dec: '12', december: '12'
        };

        // Check if Unix Timestamp in ms or seconds
        if (/^\d{9,13}$/.test(str)) {
          const ts = parseInt(str, 10);
          const ms = str.length === 10 ? ts * 1000 : ts;
          const d = new Date(ms);
          if (!isNaN(d.getTime())) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const day = String(d.getUTCDate()).padStart(2, '0');
            if (y >= 1900 && y <= 2026) return `${y}-${m}-${day}`;
          }
        }

        // Check for text months (e.g. "Jun 15 1986" or "15 June 1986")
        const words = str.toLowerCase().split(/\s+/).filter(Boolean);
        let namedMonth = null;
        let dayVal = null;
        let yearVal = null;

        for (const w of words) {
          if (monthMap[w]) {
            namedMonth = monthMap[w];
          } else {
            const num = parseInt(w.replace(/\D/g, ''), 10);
            if (!isNaN(num)) {
              if (num >= 1900 && num <= 2026) {
                yearVal = String(num);
              } else if (num >= 1 && num <= 31) {
                dayVal = String(num).padStart(2, '0');
              }
            }
          }
        }

        if (namedMonth && yearVal && dayVal) {
          return `${yearVal}-${namedMonth}-${dayVal}`;
        }

        // Strip ISO time or trailing space time
        if (str.includes('T')) str = str.split('T')[0];
        if (str.includes(' ')) {
          const firstPart = str.split(' ')[0];
          if (firstPart.includes('/') || firstPart.includes('-')) {
            str = firstPart;
          }
        }

        if (str.includes('/')) {
          const parts = str.split('/').map((p) => p.trim());
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
            const [m, d, y] = parts;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
          }
        }

        if (str.includes('-')) {
          const parts = str.split('-').map((p) => p.trim());
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
            if (parts[2].length === 4) {
              const [m, d, y] = parts;
              return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
            }
          }
        }

        // Standard JS Date parsing fallback
        try {
          const d = new Date(raw);
          if (!isNaN(d.getTime())) {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const day = String(d.getUTCDate()).padStart(2, '0');
            if (y >= 1900 && y <= 2026) {
              return `${y}-${m}-${day}`;
            }
          }
        } catch (e) {}

        return str;
      };

      const formattedDob = normalizeDOBString(dob);
      const computedAge = formattedDob ? calculateAgeFromDOB(formattedDob) : null;

      const hasRequiredStep1Data = Boolean(
        firstName && lastName && email && phone && zipCode && formattedDob && computedAge >= 18 && computedAge <= 84 && gender && coverageAmount
      );

      if (firstName || lastName || email || phone || zipCode || formattedDob || gender || rawCoverage) {
        setFormData((prev) => ({
          ...prev,
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(zipCode ? { zipCode } : {}),
          ...(formattedDob ? { dob: formattedDob, age: computedAge } : {}),
          ...(gender ? { gender } : {}),
          coverageAmount: coverageAmount || prev.coverageAmount || 50000,
          ...(hasRequiredStep1Data || stepParam === '2' ? { tcpaConsent: true } : {})
        }));

        if (formattedDob && formattedDob.includes('-')) {
          const parts = formattedDob.split('-');
          if (parts.length === 3 && parts[0].length === 4) {
            setDobParts({
              year: parts[0],
              month: parts[1].padStart(2, '0'),
              day: parts[2].padStart(2, '0')
            });
          }
        }

        if (refId) {
          setApplicationRefId(refId);
          try {
            sessionStorage.setItem('ie_lump_sum_ref_id', refId);
          } catch (e) {}
        }

        // Advance to Step 2 if all required Step 1 data is complete
        if (hasRequiredStep1Data) {
          setHasSubmittedLead(true);
          setCurrentStep(2);
          triggerToast(`Welcome back${firstName ? ', ' + firstName : ''}! Here is your estimated rate.`, 'success');
        } else {
          // If required Step 1 fields are missing, keep on Step 1 with a clear prompt
          setCurrentStep(1);
          if (!formattedDob) {
            triggerToast(`Welcome back${firstName ? ', ' + firstName : ''}! Please select your Date of Birth to view your rate.`, 'info');
          } else if (!phone) {
            triggerToast(`Welcome back${firstName ? ', ' + firstName : ''}! Please confirm your phone number to continue.`, 'info');
          } else {
            triggerToast(`Welcome back${firstName ? ', ' + firstName : ''}! Please verify your details to view your rate.`, 'info');
          }
        }

        // Security / UX Best Practice: Strip sensitive query params from browser URL bar
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {}
      }
    } catch (err) {
      console.warn('Could not parse URL follow-up params:', err);
    }
  }, []);

  // Warn user before closing or reloading the page if they have active form input
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasEnteredData =
        currentStep > 1 ||
        formData.firstName ||
        formData.lastName ||
        formData.phone ||
        formData.email ||
        formData.coverageAmount !== null ||
        formData.dob;

      if (currentStep < 5 && hasEnteredData) {
        e.preventDefault();
        e.returnValue = 'If you reload or close this page, your form answers will be lost.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep, formData]);

  // IP Address Detection
  const [ipAddress, setIpAddress] = useState('Detecting...');

  useEffect(() => {
    fetch('/wp-json/custom/v1/getIP')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.ip && data.ip !== 'Unavailable') {
          setIpAddress(data.ip);
        } else {
          throw new Error('WP IP endpoint unavailable');
        }
      })
      .catch(() => {
        fetch('https://api64.ipify.org?format=json')
          .then((res) => res.json())
          .then((data) => {
            if (data && data.ip) setIpAddress(data.ip);
            else setIpAddress('127.0.0.1');
          })
          .catch(() => setIpAddress('127.0.0.1'));
      });
  }, []);

  // Dynamic DOB Limits calculated from today's exact date (Ages 18 to 84)
  const dobLimits = useMemo(() => getDOBLimits(18, 84), []);

  // Real-time calculation engine
  const rateCalculation = useMemo(() => {
    return calculateLumpSumRate({
      age: formData.age || 38,
      coverageAmount: formData.coverageAmount || 50000
    });
  }, [formData.age, formData.coverageAmount]);

  // Handle Form Changes
  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, phone: formatted }));
    } else if (field === 'zipCode') {
      setFormData((prev) => ({ ...prev, zipCode: formatZipCode(value) }));
    } else if (field === 'dob') {
      const computedAge = calculateAgeFromDOB(value);
      if (computedAge !== null && computedAge >= 18 && computedAge <= 84) {
        setFormData((prev) => ({ ...prev, dob: value, age: computedAge }));
      } else {
        setFormData((prev) => ({ ...prev, dob: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (step1Errors[field]) {
      setStep1Errors((prev) => ({ ...prev, [field]: '' }));
    }
    if (step2Errors[field]) {
      setStep2Errors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleDobPartChange = (part, value) => {
    const updated = {
      ...dobParts,
      [part]: value
    };
    setDobParts(updated);

    const { year, month, day } = updated;
    if (year && month && day) {
      const maxDays = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
      let safeDay = day;
      if (parseInt(day, 10) > maxDays) {
        safeDay = String(maxDays).padStart(2, '0');
        setDobParts((prev) => ({ ...prev, day: safeDay }));
      }
      const fullDob = `${year}-${month.padStart(2, '0')}-${safeDay.padStart(2, '0')}`;
      const computedAge = calculateAgeFromDOB(fullDob);
      setFormData((prev) => ({
        ...prev,
        dob: fullDob,
        age: computedAge
      }));
      if (step1Errors.dob) {
        setStep1Errors((prev) => ({ ...prev, dob: '' }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        dob: '',
        age: null
      }));
    }
  };

  // Step 1 Validations
  const validateStep1 = () => {
    const errors = {};
    if (!formData.coverageAmount) {
      errors.coverageAmount = 'Please select a coverage amount';
    }
    if (!formData.dob) {
      errors.dob = 'Please select your date of birth';
    } else {
      const age = calculateAgeFromDOB(formData.dob);
      if (age === null || age < 18 || age > 84) {
        errors.dob = 'Age must be between 18 and 84';
      }
    }
    if (!formData.zipCode || !validateZip(formData.zipCode.trim())) {
      errors.zipCode = 'Please enter a valid 5-digit ZIP code';
    }
    if (!formData.gender) {
      errors.gender = 'Please select gender';
    }
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.phone || !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit cell phone';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.tcpaConsent) {
      errors.tcpaConsent = 'Please agree to the terms and communication agreement to continue.';
    }

    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    setStep2Errors({});
    return true;
  };

  // Step 3 Questionnaire Validations
  const validateQuestionnaire = () => {
    const errors = {};
    if (!questionnaire.q1_replacing_or_declined) {
      errors.q1 = 'Please answer this question';
    } else if (questionnaire.q1_replacing_or_declined === 'yes' && !questionnaire.q1_details.trim()) {
      errors.q1 = 'Please provide details for question 1';
    }

    if (!questionnaire.q2_cancer_or_tests) {
      errors.q2 = 'Please answer this question';
    } else if (questionnaire.q2_cancer_or_tests === 'yes' && !questionnaire.q2_details.trim()) {
      errors.q2 = 'Please provide details for question 2';
    }

    if (!questionnaire.q3_hiv_aids) {
      errors.q3 = 'Please answer this question';
    } else if (questionnaire.q3_hiv_aids === 'yes' && !questionnaire.q3_details.trim()) {
      errors.q3 = 'Please provide details for question 3';
    }

    setQuestionnaireErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 4 ACH & Signature Validations
  const validateAchPayment = () => {
    const errors = {};
    const cleanRouting = achPayment.routingNumber ? achPayment.routingNumber.replace(/\D/g, '') : '';
    if (!cleanRouting || cleanRouting.length !== 9) {
      errors.routingNumber = 'Please enter a valid 9-digit routing number';
    }
    if (!achPayment.bankName || !achPayment.bankName.trim()) {
      errors.bankName = 'Please enter your bank name';
    }
    const cleanAccount = achPayment.accountNumber ? achPayment.accountNumber.replace(/\D/g, '') : '';
    if (!cleanAccount || cleanAccount.length < 4) {
      errors.accountNumber = 'Please enter a valid bank account number';
    }
    if (!achPayment.bankCustomerName || !achPayment.bankCustomerName.trim()) {
      errors.bankCustomerName = 'Name appearing on bank details is required';
    }
    if (sigCanvasRef.current && sigCanvasRef.current.isEmpty()) {
      errors.signature = 'Please draw your electronic signature';
    }

    setAchErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Reset entire form and start fresh
  const handleStartNewQuote = () => {
    setFormData({
      zipCode: '',
      dob: '',
      age: null,
      gender: '',
      coverageAmount: null,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      tcpaConsent: false
    });

    setDobParts({ year: '', month: '', day: '' });

    setStep1Errors({});
    setStep2Errors({});
    setQuestionnaireErrors({});
    setAchErrors({});

    setQuestionnaire({
      q1_replacing_or_declined: '',
      q1_details: '',
      q2_cancer_or_tests: '',
      q2_details: '',
      q3_hiv_aids: '',
      q3_details: ''
    });

    setAchPayment({
      routingNumber: '',
      bankName: '',
      accountNumber: '',
      bankCustomerName: '',
      accountType: 'checking',
      signatureType: 'draw',
      achConsent: true
    });

    if (sigCanvasRef.current) {
      try {
        sigCanvasRef.current.clear();
      } catch (e) {}
    }

    setHasSubmittedLead(false);
    setIsSubmitting(false);
    setIsCalculating(false);
    setSubmissionError('');
    hideToast();
    const freshRef = generateReferenceId();
    setApplicationRefId(freshRef);
    try {
      sessionStorage.setItem('ie_lump_sum_ref_id', freshRef);
    } catch (e) {}

    try {
      setLocalStorage('ie_lump_sum_application', null);
    } catch (e) {}

    setCurrentStep(1);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleModalAgree = () => {
    setFormData((prev) => ({ ...prev, tcpaConsent: true }));
    if (step1Errors.tcpaConsent) {
      setStep1Errors((prev) => ({ ...prev, tcpaConsent: '' }));
    }
    setShowLegalModal(false);
  };

  // Step Progressions
  const handleProceedToStep2 = async () => {
    if (!validateStep1()) {
      triggerToast('Please fill in all required fields and check the agreement box.', 'warning');
      return;
    }

    // If user already submitted to GHL on Step 1, advance directly to step 2
    if (hasSubmittedLead) {
      setCurrentStep(2);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }

    // Submit lead directly in background and proceed to Step 2
    await handleAgreeAndProceed();
  };

  const handleAgreeAndProceed = async () => {
    setIsCalculating(true);
    setSubmissionError('');

    try {
      const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      let usDob = '';
      if (formData.dob && formData.dob.includes('-')) {
        const parts = formData.dob.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          usDob = `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
      }

      const payload = {
        referenceId: applicationRefId,
        refId: applicationRefId,
        ref_id: applicationRefId,
        ref: applicationRefId,
        submissionDate: formatDateTime(),
        formName: 'Cancer, Heart Attack & Stroke Lump Sum Benefits',
        formUrl: window.location.href,

        // Status Trigger Key
        submittedApplication: false,
        applicationStatus: 'Quote Generated',

        // Contact & Demographics
        firstName: formData.firstName ? formData.firstName.trim() : '',
        lastName: formData.lastName ? formData.lastName.trim() : '',
        name: `${formData.firstName ? formData.firstName.trim() : ''} ${formData.lastName ? formData.lastName.trim() : ''}`.trim(),
        email: formData.email ? formData.email.trim() : '',
        phone: formData.phone ? formData.phone.trim() : '',
        phoneRaw: cleanPhone,
        cell_phone: formData.phone ? formData.phone.trim() : '',
        cellPhone: formData.phone ? formData.phone.trim() : '',
        zipCode: formData.zipCode ? formData.zipCode.trim() : '',
        postalCode: formData.zipCode ? formData.zipCode.trim() : '',
        dateOfBirth: formData.dob || '',
        dob: formData.dob || '',
        date_of_birth: formData.dob || '',
        birth_date: formData.dob || '',
        birthday: formData.dob || '',
        dateOfBirthUS: usDob,
        dobUS: usDob,
        date_of_birth_us: usDob,
        age: formData.age || 0,
        gender: formData.gender ? (formData.gender === 'male' ? 'Male' : 'Female') : '',

        // Plan & Rate Quote
        coverageAmount: rateCalculation.coverageAmount || formData.coverageAmount || 0,
        coverage_amount: rateCalculation.coverageAmount || formData.coverageAmount || 0,
        coverage: rateCalculation.coverageAmount || formData.coverageAmount || 0,
        benefit: rateCalculation.coverageAmount || formData.coverageAmount || 0,
        plan: rateCalculation.coverageAmount || formData.coverageAmount || 0,
        cancerCoverage: rateCalculation.cancerCoverage || formData.coverageAmount || 0,
        heartStrokeCoverage: rateCalculation.heartStrokeCoverage || formData.coverageAmount || 0,
        monthlyPremium: rateCalculation.totalMonthlyPremium || 0,
        annualPremium: rateCalculation.totalAnnualPremium || 0,
        isFullUnderwriting: rateCalculation.isFullUnderwriting || false,

        // Health Questionnaire (Empty placeholders on Step 1)
        health_q1_replacing_or_declined: '',
        health_q1_details: '',
        health_q2_cancer_or_tests: '',
        health_q2_details: '',
        health_q3_hiv_aids: '',
        health_q3_details: '',

        // Banking & Signature (Empty placeholders on Step 1)
        routingNumber: '',
        bankName: '',
        accountNumber: '',
        accountNumberMasked: '',
        bankCustomerName: '',
        accountType: '',
        signatureType: '',
        signatureContent: '',
        achDisclaimer: '',

        // Google Drive Signed PDF Links (Empty placeholders on Step 1)
        lumpSumSignature: '',
        lumpSumApplicationPdf: '',
        webViewLink: '',
        webContentLink: '',

        // Compliance
        tcpaConsent: true,
        tcpaConsentTimestamp: formatDateTime(),
        ipAddress: ipAddress !== 'Detecting...' ? ipAddress : '0.0.0.0',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        exactDisclaimerText: TCPA_CALCULATOR_DISCLAIMER
      };

      await withSubmissionTimeout(async () => {
        const [accountID, webhookID] = await Promise.all([
          getAccountID(),
          getWebhookID({ formName: 'lump_sum_calculator' })
        ]);

        if (!accountID || !webhookID) {
          throw new Error('Unable to retrieve GHL account or webhook configuration.');
        }

        await sendToGHL(payload, accountID, webhookID);
      });

      // On Successful submission to GHL:
      setHasSubmittedLead(true);
      setFormData((prev) => ({ ...prev, tcpaConsent: true }));
      setShowLegalModal(false);
      triggerToast('Estimated rate calculated successfully!', 'success');
      setCurrentStep(2);
      window.scrollTo({ top: 150, behavior: 'smooth' });
    } catch (err) {
      console.error('GHL rate calculation submission error:', err);
      const errMsg = getSubmissionErrorMessage(
        err,
        'There was a problem calculating your rate. Please check your connection and try again.'
      );
      setSubmissionError(errMsg);
      triggerToast(errMsg, 'error', 6000);
      // DO NOT PROCEED TO STEP 2
    } finally {
      setIsCalculating(false);
    }
  };

  const handleProceedToStep3 = () => {
    if (!validateStep1()) {
      triggerToast('Please fill in all required fields in Step 1 first.', 'warning');
      setCurrentStep(1);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!hasSubmittedLead && !formData.tcpaConsent) {
      setLegalTab('terms');
      setShowLegalModal(true);
      return;
    }
    setCurrentStep(3);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleProceedToStep4 = () => {
    if (!validateStep1()) {
      triggerToast('Please fill in all required fields in Step 1 first.', 'warning');
      setCurrentStep(1);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!hasSubmittedLead && !formData.tcpaConsent) {
      setLegalTab('terms');
      setShowLegalModal(true);
      return;
    }
    if (!validateQuestionnaire()) {
      triggerToast('Please answer all 3 health questions in Step 3.', 'warning');
      if (currentStep !== 3) {
        setCurrentStep(3);
        window.scrollTo({ top: 150, behavior: 'smooth' });
      }
      return;
    }
    setCurrentStep(4);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };


  const handleBackToStep1 = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleBackToStep2 = () => {
    if (!validateStep1()) {
      triggerToast('Please fill in all required fields in Step 1.', 'warning');
      setCurrentStep(1);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!hasSubmittedLead && !formData.tcpaConsent) {
      setLegalTab('terms');
      setShowLegalModal(true);
      return;
    }
    setCurrentStep(2);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleBackToStep3 = () => {
    if (!validateStep1()) {
      triggerToast('Please fill in all required fields in Step 1.', 'warning');
      setCurrentStep(1);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!hasSubmittedLead && !formData.tcpaConsent) {
      setLegalTab('terms');
      setShowLegalModal(true);
      return;
    }
    setCurrentStep(3);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  // Final Submit on Step 4
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) {
      triggerToast('Please complete all contact and demographic details in Step 1.', 'warning');
      setCurrentStep(1);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!validateQuestionnaire()) {
      triggerToast('Please complete all 3 health pre-approval questions in Step 3.', 'warning');
      setCurrentStep(3);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }
    if (!validateAchPayment()) {
      triggerToast('Please complete authorization and signature fields in Step 4.', 'warning');
      return;
    }

    setIsSubmitting(true);
    setSubmissionError('');

    const refNumber = applicationRefId || generateReferenceId();
    if (!applicationRefId) {
      setApplicationRefId(refNumber);
    }

    let signatureImageOrText = achPayment.typedSignature;
    if (achPayment.signatureType === 'draw' && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      signatureImageOrText = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');
    }
    setDocSignatureImage(signatureImageOrText);

    // Allow DOM to update signature image for PDF capture
    await new Promise((resolve) => setTimeout(resolve, 150));

    let gdriveLink = '';
    let gdriveContentLink = '';

    try {
      await withSubmissionTimeout(async () => {
        try {
          if (applicationDocRef.current) {
            const canvas = await html2canvas(applicationDocRef.current, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: false
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'letter', true);
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
            const pdfBlob = pdf.output('blob');

            const uploadData = new FormData();
            const safeLastName = (formData.lastName || 'Applicant').replace(/[^a-zA-Z0-9]/g, '');
            const safeFirstName = (formData.firstName || '').replace(/[^a-zA-Z0-9]/g, '');
            const fileName = `LumpSum-Application-${safeLastName}-${safeFirstName}-${refNumber}.pdf`;

            uploadData.append('file', pdfBlob, fileName);
            uploadData.append('userEmail', formData.email ? formData.email.trim() : '');
            uploadData.append('formType', 'lump_sum');

            console.log('Dispatching PDF upload to /custom/v1/upload-lumpsum:', fileName);
            const uploadRes = await postWP('custom/v1/upload-lumpsum', uploadData);
            console.log('Lump Sum Upload API Response:', uploadRes);

            if (uploadRes && uploadRes.success && uploadRes.link) {
              gdriveLink = uploadRes.link;
              gdriveContentLink = uploadRes.webContentLink || uploadRes.link;
            } else if (uploadRes && uploadRes.webViewLink) {
              gdriveLink = uploadRes.webViewLink;
              gdriveContentLink = uploadRes.webContentLink || uploadRes.webViewLink;
            } else if (uploadRes && uploadRes.message) {
              console.error('Google Drive Upload error message:', uploadRes.message);
            }
          } else {
            console.warn('applicationDocRef is not attached to DOM node.');
          }
        } catch (pdfErr) {
          console.error('PDF Generation / Google Drive upload exception:', pdfErr);
          if (
            (typeof navigator !== 'undefined' && !navigator.onLine) ||
            pdfErr?.code === 'ECONNABORTED' ||
            pdfErr?.code === 'ERR_NETWORK'
          ) {
            throw pdfErr;
          }
        }

        const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
        let usDob = '';
        if (formData.dob && formData.dob.includes('-')) {
          const parts = formData.dob.split('-');
          if (parts.length === 3 && parts[0].length === 4) {
            usDob = `${parts[1]}/${parts[2]}/${parts[0]}`;
          }
        }

        const payload = {
          referenceId: refNumber,
          refId: refNumber,
          ref_id: refNumber,
          ref: refNumber,
          submissionDate: formatDateTime(),
          formName: 'Cancer, Heart Attack & Stroke Lump Sum Benefits',
          formUrl: window.location.href,

          // Status Trigger Key
          submittedApplication: true,
          applicationStatus: 'Application & Payment Submitted',

          // Contact & Demographics
          firstName: formData.firstName ? formData.firstName.trim() : '',
          lastName: formData.lastName ? formData.lastName.trim() : '',
          name: `${formData.firstName ? formData.firstName.trim() : ''} ${formData.lastName ? formData.lastName.trim() : ''}`.trim(),
          email: formData.email ? formData.email.trim() : '',
          phone: formData.phone ? formData.phone.trim() : '',
          phoneRaw: cleanPhone,
          cell_phone: formData.phone ? formData.phone.trim() : '',
          cellPhone: formData.phone ? formData.phone.trim() : '',
          zipCode: formData.zipCode ? formData.zipCode.trim() : '',
          postalCode: formData.zipCode ? formData.zipCode.trim() : '',
          dateOfBirth: formData.dob || '',
          dob: formData.dob || '',
          date_of_birth: formData.dob || '',
          birth_date: formData.dob || '',
          birthday: formData.dob || '',
          dateOfBirthUS: usDob,
          dobUS: usDob,
          date_of_birth_us: usDob,
          age: formData.age || 0,
          gender: formData.gender ? (formData.gender === 'male' ? 'Male' : 'Female') : '',

          // Plan & Rate Quote
          coverageAmount: rateCalculation.coverageAmount || 0,
          coverage_amount: rateCalculation.coverageAmount || 0,
          coverage: rateCalculation.coverageAmount || 0,
          benefit: rateCalculation.coverageAmount || 0,
          plan: rateCalculation.coverageAmount || 0,
          cancerCoverage: rateCalculation.cancerCoverage || 0,
          heartStrokeCoverage: rateCalculation.heartStrokeCoverage || 0,
          monthlyPremium: rateCalculation.totalMonthlyPremium || 0,
          annualPremium: rateCalculation.totalAnnualPremium || 0,
          isFullUnderwriting: rateCalculation.isFullUnderwriting || false,

          // Health Questionnaire
          health_q1_replacing_or_declined: questionnaire.q1_replacing_or_declined || 'no',
          health_q1_details: questionnaire.q1_details || '',
          health_q2_cancer_or_tests: questionnaire.q2_cancer_or_tests || 'no',
          health_q2_details: questionnaire.q2_details || '',
          health_q3_hiv_aids: questionnaire.q3_hiv_aids || 'no',
          health_q3_details: questionnaire.q3_details || '',

          // Banking & Signature
          routingNumber: achPayment.routingNumber ? achPayment.routingNumber.trim() : '',
          bankName: achPayment.bankName ? achPayment.bankName.trim() : '',
          accountNumber: achPayment.accountNumber ? achPayment.accountNumber.trim() : '',
          accountNumberMasked: achPayment.accountNumber ? achPayment.accountNumber.trim() : '',
          bankCustomerName: achPayment.bankCustomerName ? achPayment.bankCustomerName.trim() : '',
          accountType: achPayment.accountType || 'checking',
          signatureType: achPayment.signatureType || 'draw',
          signatureContent: signatureImageOrText || '',
          achDisclaimer: ACH_DISCLAIMER_TEXT,

          // Google Drive Signed PDF Links (lumpSumSignature & lumpSumApplicationPdf)
          lumpSumSignature: gdriveLink || '',
          lumpSumApplicationPdf: gdriveLink || '',
          webViewLink: gdriveLink || '',
          webContentLink: gdriveContentLink || '',

          // Compliance
          tcpaConsent: true,
          tcpaConsentTimestamp: formatDateTime(),
          ipAddress: ipAddress !== 'Detecting...' ? ipAddress : '0.0.0.0',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          exactDisclaimerText: TCPA_CALCULATOR_DISCLAIMER
        };

        setLocalStorage('ie_lump_sum_application', payload);

        const [accountId, webhookId] = await Promise.all([
          getAccountID(),
          getWebhookID({ formName: 'lump_sum_calculator' })
        ]);
        if (!accountId || !webhookId) {
          throw new Error('Unable to retrieve GHL account or webhook configuration.');
        }
        await sendToGHL(payload, accountId, webhookId);
      });

      triggerToast(`Application submitted successfully! Ref: #${refNumber}`, 'success', 5000);
      setCurrentStep(5);
      window.scrollTo({ top: 150, behavior: 'smooth' });
    } catch (err) {
      console.error('Submission error:', err);
      const errMsg = getSubmissionErrorMessage(
        err,
        'Unable to submit your application. Please check your connection and try again.'
      );
      setSubmissionError(errMsg);
      triggerToast(errMsg, 'error', 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen py-6 sm:py-10 pb-16 sm:pb-20 px-2 sm:px-4 overflow-x-clip">
      <PageLayout
        content={
          <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">

            {/* Title Header (Themed like Health Plan Calculator) */}
            <div className="text-center space-y-2.5 max-w-3xl mx-auto px-2">
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-base-content tracking-tight leading-tight">
                Health Insurance Pays Your Doctors. <span className="text-[#2AA107]">We Help Protect Your Paycheck.</span>
              </h1>
              <p className="text-xs sm:text-sm text-base-content/80 max-w-2xl mx-auto leading-relaxed">
                Up to $150,000 in cash paid directly to you upon diagnosis of Cancer, Heart Attack, or Stroke. Starting at just <strong>$7.25/month</strong>.
              </p>
              <div className="pt-2 sm:pt-3">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById('how-it-works-scenarios');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="relative inline-flex items-center gap-2.5 text-xs sm:text-sm md:text-base font-extrabold text-[#2AA107] hover:text-white bg-[#2AA107]/10 hover:bg-[#2AA107] border-2 border-[#2AA107]/40 hover:border-[#2AA107] py-2 px-5 sm:px-7 rounded-full shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 cursor-pointer group"
                >
                  {/* Subtle pulsing live indicator dot */}
                  <span className="relative flex h-3 w-3 sm:h-3.5 sm:w-3.5 items-center justify-center shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2AA107] opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-[#2AA107] group-hover:bg-white transition-colors"></span>
                  </span>
                  <span>See How It Works: Real-World Claim Examples</span>
                  <FaArrowRight className="text-xs sm:text-sm group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* DaisyUI Steps Navigation Bar (Only for Steps 1 - 4) */}
            {currentStep < 5 && (
              <div className="w-full max-w-xl mx-auto px-2 pointer-events-none">
                <ul className="steps steps-horizontal w-full text-[11px] sm:text-xs font-semibold select-none">
                  <li 
                    className={`step ${
                      currentStep >= 1 ? 'step-secondary' : ''
                    }`}
                  />
                  <li 
                    className={`step ${
                      currentStep >= 2 ? 'step-secondary' : ''
                    }`}
                  />
                  <li 
                    className={`step ${
                      currentStep >= 3 ? 'step-secondary' : ''
                    }`}
                  />
                  <li 
                    className={`step ${
                      currentStep >= 4 ? 'step-secondary' : ''
                    }`}
                  />
                </ul>
              </div>
            )}

            {/* ================= OVERLAPPING CAROUSEL TRACK ================= */}
            <div className="relative w-full max-w-3xl lg:max-w-4xl mx-auto py-2 sm:py-4">

              {/* Carousel Left Navigation Arrow */}
              {currentStep > 1 && currentStep < 5 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (currentStep === 2) handleBackToStep1();
                    else if (currentStep === 3) handleBackToStep2();
                    else if (currentStep === 4) handleBackToStep3();
                  }}
                  className="hidden md:flex absolute left-1 lg:-left-3 xl:-left-5 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-sm sm:btn-md bg-white/95 hover:bg-white shadow-xl border border-slate-200 text-slate-700 hover:text-[#2AA107] hover:scale-110 active:scale-95 transition-all items-center justify-center cursor-pointer pointer-events-auto"
                  title="Previous Step"
                  aria-label="Previous Step"
                >
                  <FaChevronLeft className="text-xs sm:text-sm" />
                </button>
              )}

              {/* Carousel Right Navigation Arrow */}
              {currentStep < 4 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (currentStep === 1) handleProceedToStep2();
                    else if (currentStep === 2) handleProceedToStep3();
                    else if (currentStep === 3) handleProceedToStep4();
                  }}
                  className="hidden md:flex absolute right-1 lg:-right-3 xl:-right-5 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-sm sm:btn-md bg-white/95 hover:bg-white shadow-xl border border-slate-200 text-slate-700 hover:text-[#2AA107] hover:scale-110 active:scale-95 transition-all items-center justify-center cursor-pointer pointer-events-auto"
                  title="Next Step"
                  aria-label="Next Step"
                >
                  <FaChevronRight className="text-xs sm:text-sm" />
                </button>
              )}

              {/* Stacked Carousel Deck Container */}
              <div className="relative w-full transition-all duration-500 pb-2">

                {/* --- SLIDE 1: COVERAGE & DEMOGRAPHICS CARD --- */}
                <div
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 1
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 2
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[70%] sm:-translate-x-[70%] md:-translate-x-[68%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep > 1) {
                      handleBackToStep1();
                    }
                  }}
                >
                  {currentStep === 1 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between gap-2 border-b border-base-200 pb-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0 shadow-xs shrink-0 whitespace-nowrap px-2.5 py-1">
                            Step 1
                          </span>
                          <h2 className="text-sm sm:text-base font-bold text-base-content leading-snug truncate sm:whitespace-normal">
                            Select Your Coverage and Details
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium shrink-0 whitespace-nowrap">1 of 4</span>
                      </div>

                      {/* Form Inputs */}
                      <div className="space-y-4">
                        {/* Coverage Selector Buttons */}
                        <div>
                          <div className="flex items-center justify-between pb-2.5">
                            <div className="flex items-center gap-1.5">
                              <label className="label py-0 cursor-pointer">
                                <span className="label-text font-semibold text-xs text-base-content">
                                  Coverage Amount <span className="text-error">*</span>
                                </span>
                              </label>
                              <div
                                className="tooltip tooltip-bottom sm:tooltip-right z-40 before:text-xs before:max-w-xs before:whitespace-normal before:text-left before:p-2.5 before:leading-relaxed before:shadow-lg"
                                data-tip="This is the lump-sum cash benefit paid directly to you if you are ever diagnosed with Cancer, Heart Attack, or Stroke."
                              >
                                <button
                                  type="button"
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-base-content/50 hover:text-[#2AA107] hover:bg-base-200/80 transition-all cursor-pointer"
                                  aria-label="Coverage Amount Information"
                                >
                                  <FaInfoCircle className="text-sm" />
                                </button>
                              </div>
                            </div>
                            {formData.coverageAmount && (
                              <span className="text-xs font-bold text-[#2AA107]">
                                Selected: ${formData.coverageAmount.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-2.5 mt-1">
                            {COVERAGE_TIERS.map((tier) => {
                              const isSelected = Number(formData.coverageAmount) === Number(tier.value);
                              return (
                                <button
                                  key={tier.value}
                                  type="button"
                                  onClick={() => handleInputChange('coverageAmount', tier.value)}
                                  className={`btn btn-sm text-xs rounded-xl transition-all cursor-pointer h-10 min-h-[40px] ${
                                    isSelected
                                      ? 'bg-[#2AA107] hover:bg-[#238806] text-white border-0 font-black shadow-xs ring-2 ring-[#2AA107]/30 scale-[1.02]'
                                      : 'btn-outline border-base-300 bg-base-100 hover:bg-base-200 text-base-content font-bold'
                                  }`}
                                >
                                  {tier.label}
                                </button>
                              );
                            })}
                          </div>
                          {step1Errors.coverageAmount && (
                            <span className="text-xs text-error mt-1 block">{step1Errors.coverageAmount}</span>
                          )}
                        </div>

                        {/* Over $100k Notice */}
                        {rateCalculation.isFullUnderwriting && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900 text-xs flex items-start gap-2">
                            <FaExclamationTriangle className="text-amber-600 text-xs shrink-0 mt-0.5" />
                            <span className="text-xs leading-relaxed">
                              <strong>Note:</strong> Total combined benefits exceeding $100,000 will require full underwriting, including a prescription report, MIB check, and phone interview based on findings.
                            </span>
                          </div>
                        )}

                        {/* DOB, ZIP, Gender - Proportional 12-col Grid with Clean Spacing */}
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5">
                          {/* Date of Birth (6 cols with 3 Dropdowns) */}
                          <div className="sm:col-span-6 space-y-1.5">
                            <div className="flex items-center justify-between min-h-[20px]">
                              <label className="text-xs font-semibold text-base-content">
                                Date of Birth <span className="text-error">*</span>
                              </label>
                              {formData.age && (
                                <span className="text-[11px] text-[#2AA107] font-bold">
                                  Age: {formData.age} yrs ✓
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-12 gap-1.5">
                              {/* Month Dropdown (Word Month) */}
                              <div className="col-span-5">
                                <select
                                  style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23475569'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E")`,
                                    backgroundPosition: 'right 0.35rem center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: '1rem 1rem',
                                    paddingRight: '1.5rem',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none'
                                  }}
                                  className={`select select-bordered select-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl pl-2.5 ${
                                    !dobParts.month ? 'font-bold text-slate-900' : 'font-normal text-slate-700'
                                  }`}
                                  value={dobParts.month}
                                  onChange={(e) => handleDobPartChange('month', e.target.value)}
                                >
                                  <option value="" className="font-bold">Month</option>
                                  {MONTHS.map((m) => (
                                    <option key={m.value} value={m.value} className="font-normal">
                                      {m.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Day Dropdown */}
                              <div className="col-span-3">
                                <select
                                  style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23475569'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E")`,
                                    backgroundPosition: 'right 0.35rem center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: '1rem 1rem',
                                    paddingRight: '1.5rem',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none'
                                  }}
                                  className={`select select-bordered select-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl pl-2.5 ${
                                    !dobParts.day ? 'font-bold text-slate-900' : 'font-normal text-slate-700'
                                  }`}
                                  value={dobParts.day}
                                  onChange={(e) => handleDobPartChange('day', e.target.value)}
                                >
                                  <option value="" className="font-bold">Day</option>
                                  {DAYS.map((d) => (
                                    <option key={d} value={d} className="font-normal">
                                      {parseInt(d, 10)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Year Dropdown */}
                              <div className="col-span-4">
                                <select
                                  style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23475569'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E")`,
                                    backgroundPosition: 'right 0.35rem center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: '1rem 1rem',
                                    paddingRight: '1.5rem',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none'
                                  }}
                                  className={`select select-bordered select-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl pl-2.5 ${
                                    !dobParts.year ? 'font-bold text-slate-900' : 'font-normal text-slate-700'
                                  }`}
                                  value={dobParts.year}
                                  onChange={(e) => handleDobPartChange('year', e.target.value)}
                                >
                                  <option value="" className="font-bold">Year</option>
                                  {dobLimits.years.map((yr) => (
                                    <option key={yr} value={String(yr)} className="font-normal">
                                      {yr}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {step1Errors.dob && <span className="text-xs text-error mt-1 block">{step1Errors.dob}</span>}
                          </div>

                          {/* ZIP Code (3 cols) */}
                          <div className="sm:col-span-3 space-y-1.5">
                            <div className="flex items-center justify-between min-h-[20px]">
                              <label className="text-xs font-semibold text-base-content">
                                ZIP Code <span className="text-error">*</span>
                              </label>
                            </div>
                            <input
                              type="text"
                              maxLength={5}
                              placeholder="e.g. 33101"
                              className={`input input-bordered input-sm w-full text-xs font-medium focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                                step1Errors.zipCode ? 'input-error' : ''
                              }`}
                              value={formData.zipCode}
                              onChange={(e) => handleInputChange('zipCode', e.target.value)}
                            />
                            {step1Errors.zipCode && <span className="text-xs text-error mt-1 block">{step1Errors.zipCode}</span>}
                          </div>

                          {/* Gender (3 cols) */}
                          <div className="sm:col-span-3 space-y-1.5">
                            <div className="flex items-center justify-between min-h-[20px]">
                              <label className="text-xs font-semibold text-base-content">
                                Gender <span className="text-error">*</span>
                              </label>
                            </div>
                            <div className="flex space-x-1">
                              {['female', 'male'].map((g) => (
                                <button
                                  key={g}
                                  type="button"
                                  onClick={() => handleInputChange('gender', g)}
                                  className={`flex-1 py-1.5 rounded-xl border text-xs capitalize font-bold transition-all cursor-pointer ${
                                    formData.gender === g
                                      ? 'border-[#2AA107] bg-[#2AA107] text-white shadow-xs'
                                      : 'border-base-200 bg-base-100 text-base-content/80 hover:border-base-300'
                                  }`}
                                >
                                  {g}
                                </button>
                              ))}
                            </div>
                            {step1Errors.gender && <span className="text-xs text-error mt-1 block">{step1Errors.gender}</span>}
                          </div>
                        </div>

                        {/* Contact details Section */}
                        <div className="pt-2.5 border-t border-base-200 space-y-3">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-base-content/80">
                            Contact details
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="label py-1">
                                <span className="label-text font-semibold text-xs text-base-content">
                                  First Name <span className="text-error">*</span>
                                </span>
                              </label>
                              <input
                                type="text"
                                placeholder="First name"
                                className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                                  step1Errors.firstName ? 'input-error' : ''
                                }`}
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                              />
                              {step1Errors.firstName && <span className="text-xs text-error mt-1 block">{step1Errors.firstName}</span>}
                            </div>

                            <div>
                              <label className="label py-1">
                                <span className="label-text font-semibold text-xs text-base-content">
                                  Last Name <span className="text-error">*</span>
                                </span>
                              </label>
                              <input
                                type="text"
                                placeholder="Last name"
                                className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                                  step1Errors.lastName ? 'input-error' : ''
                                }`}
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                              />
                              {step1Errors.lastName && <span className="text-xs text-error mt-1 block">{step1Errors.lastName}</span>}
                            </div>

                            <div>
                              <label className="label py-1">
                                <span className="label-text font-semibold text-xs text-base-content">
                                  Cell Phone <span className="text-error">*</span>
                                </span>
                              </label>
                              <input
                                type="tel"
                                maxLength={14}
                                placeholder="(555) 000-0000"
                                className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                                  step1Errors.phone ? 'input-error' : ''
                                }`}
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                              />
                              {step1Errors.phone && <span className="text-xs text-error mt-1 block">{step1Errors.phone}</span>}
                            </div>

                            <div>
                              <label className="label py-1">
                                <span className="label-text font-semibold text-xs text-base-content">
                                  Email Address <span className="text-error">*</span>
                                </span>
                              </label>
                              <input
                                type="email"
                                placeholder="name@example.com"
                                className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                                  step1Errors.email ? 'input-error' : ''
                                }`}
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                              />
                              {step1Errors.email && <span className="text-xs text-error mt-1 block">{step1Errors.email}</span>}
                            </div>
                          </div>
                        </div>

                        {/* Mandatory TCPA Checkbox */}
                        <div className="pt-2.5 border-t border-base-200">
                          <label className="label cursor-pointer flex items-start space-x-2 p-0">
                            <input
                              type="checkbox"
                              disabled={isCalculating}
                              name="tcpaConsent"
                              checked={formData.tcpaConsent || false}
                              onChange={(e) => handleInputChange('tcpaConsent', e.target.checked)}
                              className={`checkbox checkbox-primary checkbox-sm rounded mt-0.5 ${
                                step1Errors.tcpaConsent ? 'checkbox-error' : ''
                              }`}
                            />
                            <span className="label-text text-[11px] leading-relaxed text-base-content/85">
                              I agree to the{' '}
                              <button
                                type="button"
                                disabled={isCalculating}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openLegalModal('terms');
                                }}
                                className="underline font-bold text-[#2AA107] hover:opacity-80 cursor-pointer inline text-left bg-transparent border-0 p-0"
                              >
                                Terms &amp; Conditions
                              </button>
                              ,{' '}
                              <button
                                type="button"
                                disabled={isCalculating}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openLegalModal('privacy');
                                }}
                                className="underline font-bold text-[#2AA107] hover:opacity-80 cursor-pointer inline text-left bg-transparent border-0 p-0"
                              >
                                Privacy Policy
                              </button>
                              , and{' '}
                              <button
                                type="button"
                                disabled={isCalculating}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openLegalModal('comm');
                                }}
                                className="underline font-bold text-[#2AA107] hover:opacity-80 cursor-pointer inline text-left bg-transparent border-0 p-0"
                              >
                                Communication Agreement
                              </button>
                              . By checking this box and clicking "Calculate Estimated Rate", I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. <strong className="text-base-content">Consent is not a condition of purchase.</strong>
                            </span>
                          </label>
                          {step1Errors.tcpaConsent && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.tcpaConsent}</span>
                          )}
                        </div>
                      </div>

                      {/* Step 1 Submit Button */}
                      <div className="pt-2">
                        <button
                          type="button"
                          disabled={!formData.tcpaConsent || isCalculating}
                          onClick={handleProceedToStep2}
                          className={`btn w-full text-xs font-black uppercase tracking-wider rounded-xl py-3 h-auto transition-all ${
                            formData.tcpaConsent && !isCalculating
                              ? 'btn-secondary text-white shadow-md hover:scale-[1.01] active:scale-[0.99] cursor-pointer'
                              : isCalculating
                              ? 'btn-secondary text-white opacity-85 cursor-wait'
                              : 'btn-disabled opacity-40 bg-base-300 text-base-content/40 cursor-not-allowed border-0'
                          }`}
                        >
                          {isCalculating ? (
                            <span className="inline-flex items-center space-x-2">
                              <span className="loading loading-spinner loading-xs"></span>
                              <span>Calculating...</span>
                            </span>
                          ) : (
                            <span>Calculate Estimated Rate</span>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Peeking Summary for Step 1 */
                    <div 
                      onClick={handleBackToStep1}
                      className="flex flex-col justify-between h-full space-y-4 py-1 text-left cursor-pointer"
                    >
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge bg-[#2AA107] badge-sm text-white font-bold border-0 shrink-0 whitespace-nowrap px-2.5 py-1">✓ Step 1</span>
                        <span className="text-xs text-[#2AA107] font-bold shrink-0">Edit ✎</span>
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Coverage and Contact</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs text-base-content/85">
                          <p><strong>Plan:</strong> ${(formData.coverageAmount || 50000).toLocaleString()} Benefit</p>
                          <p><strong>Name:</strong> {formData.firstName || 'Applicant'} {formData.lastName}</p>
                          <p><strong>Age:</strong> {formData.age || '38'} yrs • <strong>ZIP:</strong> {formData.zipCode || '33101'}</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline border-[#2AA107] text-[#2AA107] hover:bg-[#2AA107] hover:text-white btn-xs font-bold w-full rounded-xl">Edit Details ✎</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 2: ESTIMATED RATE CARD --- */}
                <div
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 2
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 1
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[30%] sm:-translate-x-[30%] md:-translate-x-[32%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : currentStep === 3
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[70%] sm:-translate-x-[70%] md:-translate-x-[68%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep === 1) handleProceedToStep2();
                    else if (currentStep > 2) handleBackToStep2();
                  }}
                >
                  {currentStep === 2 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between gap-2 border-b border-base-200 pb-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0 shadow-xs shrink-0 whitespace-nowrap px-2.5 py-1">
                            Step 2
                          </span>
                          <h2 className="text-sm sm:text-base font-bold text-base-content leading-snug">
                            Your Estimated Rate
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium shrink-0 whitespace-nowrap">2 of 4</span>
                      </div>

                      {/* Enlarged Calculated Premium Banner - Green Themed */}
                      <div className="bg-gradient-to-b from-[#2AA107]/10 via-emerald-50/40 to-white border-2 border-[#2AA107]/30 rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center shadow-md space-y-4">
                        <div className="text-xs sm:text-sm uppercase font-extrabold text-slate-500 tracking-wider">
                          Your Estimated Rate:
                        </div>

                        <div className="flex items-baseline justify-center gap-2">
                          <div className="text-5xl sm:text-7xl font-black text-[#2AA107] tracking-tight leading-none">
                            ${rateCalculation.totalMonthlyPremium.toFixed(2)}
                          </div>
                          <span className="text-base sm:text-2xl font-bold text-slate-600">/ month</span>
                        </div>

                        <div className="pt-2 flex justify-center">
                          <div className="inline-flex items-center bg-white border border-[#2AA107]/30 shadow-xs rounded-full px-5 py-2">
                            <span className="text-sm sm:text-base font-black text-slate-800">
                              ${rateCalculation.coverageAmount.toLocaleString()}{' '}
                              <span className="text-xs sm:text-sm text-[#2AA107] font-bold">Lump Sum Benefits</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="flex items-center gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleBackToStep1}
                          className="btn btn-outline border-base-300 text-base-content/80 hover:bg-base-200 hover:text-base-content w-1/3 text-xs font-bold rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                        >
                          <FaArrowLeft className="text-[10px]" />
                          <span>Back</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleProceedToStep3}
                          className="btn flex-1 btn-secondary text-white font-black uppercase text-xs tracking-wider shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <span>CONTINUE ENROLLMENT</span>
                          <FaArrowRight className="text-xs" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Peeking Summary for Step 2 */
                    <div 
                      onClick={() => {
                        if (currentStep > 2) handleBackToStep2();
                        else handleProceedToStep2();
                      }}
                      className="flex flex-col justify-between h-full space-y-4 py-1 text-left cursor-pointer"
                    >
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge bg-[#2AA107] badge-sm text-white font-bold border-0 shrink-0 whitespace-nowrap px-2.5 py-1">
                          {currentStep > 2 ? '✓ Step 2' : 'Step 2'}
                        </span>
                        {currentStep > 2 && <span className="text-xs text-[#2AA107] font-bold shrink-0">Edit ✎</span>}
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Estimated Rate</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs text-base-content/85">
                          <p><strong>Estimated Rate:</strong> ${rateCalculation.totalMonthlyPremium.toFixed(2)}/mo</p>
                          <p><strong>Plan:</strong> ${rateCalculation.coverageAmount.toLocaleString()} Dual Protection</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline border-[#2AA107] text-[#2AA107] hover:bg-[#2AA107] hover:text-white btn-xs font-bold w-full rounded-xl">
                          {currentStep > 2 ? 'Edit Rate ✎' : 'View Estimated Rate →'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 3: 3 HEALTH PRE-APPROVAL QUESTIONS CARD --- */}
                <div
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 3
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 2
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[30%] sm:-translate-x-[30%] md:-translate-x-[32%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : currentStep === 4
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[70%] sm:-translate-x-[70%] md:-translate-x-[68%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep < 3) handleProceedToStep3();
                    else if (currentStep > 3) handleBackToStep3();
                  }}
                >
                  {currentStep === 3 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between gap-2 border-b border-base-200 pb-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0 shadow-xs shrink-0 whitespace-nowrap px-2.5 py-1">
                            Step 3
                          </span>
                          <h2 className="text-sm sm:text-base font-bold text-base-content leading-snug">
                            3 Health Pre-Approval Questions
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium shrink-0 whitespace-nowrap">3 of 4</span>
                      </div>

                      {/* 3 Questions */}
                      <div className="space-y-3">
                        {/* Q1 */}
                        <div className={`p-4 rounded-2xl border transition-all ${questionnaire.q1_replacing_or_declined === 'yes' ? 'border-amber-300 bg-amber-50/40' : 'border-base-200 bg-base-100'}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-base-content font-medium leading-relaxed">
                                Are you replacing existing insurance, currently on Medicare/Medicaid, or have you ever been declined for health coverage?
                              </p>
                            </div>
                            <div className="flex space-x-1.5 shrink-0 self-end sm:self-center">
                              {['no', 'yes'].map((val) => (
                                <button
                                  key={val}
                                  type="button"
                                  onClick={() => {
                                    setQuestionnaire((prev) => ({ ...prev, q1_replacing_or_declined: val }));
                                    if (questionnaireErrors.q1) setQuestionnaireErrors((prev) => ({ ...prev, q1: '' }));
                                  }}
                                  className={`px-4 py-1.5 rounded-xl border text-xs font-black uppercase transition-all cursor-pointer ${
                                    questionnaire.q1_replacing_or_declined === val
                                      ? val === 'no' ? 'bg-[#2AA107] text-white border-[#2AA107]' : 'bg-amber-600 text-white border-amber-600'
                                      : 'bg-base-200 text-base-content/80 border-base-300 hover:border-base-content/20'
                                  }`}
                                >
                                  {val}
                                </button>
                              ))}
                            </div>
                          </div>
                          {questionnaireErrors.q1 && <span className="text-xs text-error mt-1 block">{questionnaireErrors.q1}</span>}
                          {questionnaire.q1_replacing_or_declined === 'yes' && (
                            <div className="mt-2 pt-2 border-t border-amber-200 animate-fadeIn">
                              <label className="text-[11px] font-bold text-base-content block mb-1">
                                Please provide details:
                              </label>
                              <textarea
                                rows={2}
                                placeholder="Please provide details..."
                                className="textarea textarea-bordered textarea-xs w-full text-xs rounded-xl"
                                value={questionnaire.q1_details}
                                onChange={(e) => setQuestionnaire((prev) => ({ ...prev, q1_details: e.target.value }))}
                              />
                            </div>
                          )}
                        </div>

                        {/* Q2 */}
                        <div className={`p-4 rounded-2xl border transition-all ${questionnaire.q2_cancer_or_tests === 'yes' ? 'border-amber-300 bg-amber-50/40' : 'border-base-200 bg-base-100'}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-base-content font-medium leading-relaxed">
                                In the past 5 years, have you had cancer (including melanoma/leukemia)—OR in the past 12 months, had pending/abnormal tests (biopsy, mammogram, PSA, scope), unexplained weight loss, or untreated lumps?
                              </p>
                            </div>
                            <div className="flex space-x-1.5 shrink-0 self-end sm:self-center">
                              {['no', 'yes'].map((val) => (
                                <button
                                  key={val}
                                  type="button"
                                  onClick={() => {
                                    setQuestionnaire((prev) => ({ ...prev, q2_cancer_or_tests: val }));
                                    if (questionnaireErrors.q2) setQuestionnaireErrors((prev) => ({ ...prev, q2: '' }));
                                  }}
                                  className={`px-4 py-1.5 rounded-xl border text-xs font-black uppercase transition-all cursor-pointer ${
                                    questionnaire.q2_cancer_or_tests === val
                                      ? val === 'no' ? 'bg-[#2AA107] text-white border-[#2AA107]' : 'bg-amber-600 text-white border-amber-600'
                                      : 'bg-base-200 text-base-content/80 border-base-300 hover:border-base-content/20'
                                  }`}
                                >
                                  {val}
                                </button>
                              ))}
                            </div>
                          </div>
                          {questionnaireErrors.q2 && <span className="text-xs text-error mt-1 block">{questionnaireErrors.q2}</span>}
                          {questionnaire.q2_cancer_or_tests === 'yes' && (
                            <div className="mt-2 pt-2 border-t border-amber-200 animate-fadeIn">
                              <label className="text-[11px] font-bold text-base-content block mb-1">
                                Please detail condition, test date, or findings:
                              </label>
                              <textarea
                                rows={2}
                                placeholder="Please detail condition, test date, or findings..."
                                className="textarea textarea-bordered textarea-xs w-full text-xs rounded-xl"
                                value={questionnaire.q2_details}
                                onChange={(e) => setQuestionnaire((prev) => ({ ...prev, q2_details: e.target.value }))}
                              />
                            </div>
                          )}
                        </div>

                        {/* Q3 */}
                        <div className={`p-4 rounded-2xl border transition-all ${questionnaire.q3_hiv_aids === 'yes' ? 'border-amber-300 bg-amber-50/40' : 'border-base-200 bg-base-100'}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-base-content font-medium leading-relaxed">
                                Have you ever been diagnosed with, treated for, or tested positive for HIV, AIDS, or ARC?
                              </p>
                            </div>
                            <div className="flex space-x-1.5 shrink-0 self-end sm:self-center">
                              {['no', 'yes'].map((val) => (
                                <button
                                  key={val}
                                  type="button"
                                  onClick={() => {
                                    setQuestionnaire((prev) => ({ ...prev, q3_hiv_aids: val }));
                                    if (questionnaireErrors.q3) setQuestionnaireErrors((prev) => ({ ...prev, q3: '' }));
                                  }}
                                  className={`px-4 py-1.5 rounded-xl border text-xs font-black uppercase transition-all cursor-pointer ${
                                    questionnaire.q3_hiv_aids === val
                                      ? val === 'no' ? 'bg-[#2AA107] text-white border-[#2AA107]' : 'bg-amber-600 text-white border-amber-600'
                                      : 'bg-base-200 text-base-content/80 border-base-300 hover:border-base-content/20'
                                  }`}
                                >
                                  {val}
                                </button>
                              ))}
                            </div>
                          </div>
                          {questionnaireErrors.q3 && <span className="text-xs text-error mt-1 block">{questionnaireErrors.q3}</span>}
                          {questionnaire.q3_hiv_aids === 'yes' && (
                            <div className="mt-2 pt-2 border-t border-amber-200 animate-fadeIn">
                              <label className="text-[11px] font-bold text-base-content block mb-1">
                                Please provide diagnosis date:
                              </label>
                              <input
                                type="date"
                                max={new Date().toISOString().split('T')[0]}
                                className="input input-bordered input-sm sm:input-xs w-full text-xs font-semibold rounded-xl focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                value={questionnaire.q3_details}
                                onChange={(e) => setQuestionnaire((prev) => ({ ...prev, q3_details: e.target.value }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="flex items-center gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleBackToStep2}
                          className="btn btn-outline border-base-300 text-base-content/80 hover:bg-base-200 hover:text-base-content w-1/3 text-xs font-bold rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                        >
                          <FaArrowLeft className="text-[10px]" />
                          <span>Back</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleProceedToStep4}
                          className="btn flex-1 btn-secondary text-white font-black uppercase text-xs tracking-wider shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <span>PROCEED TO PAYMENT</span>
                          <FaArrowRight className="text-[10px]" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Peeking Summary for Step 3 */
                    <div 
                      onClick={() => {
                        if (currentStep > 3) handleBackToStep3();
                        else handleProceedToStep3();
                      }}
                      className="flex flex-col justify-between h-full space-y-4 py-1 text-left cursor-pointer"
                    >
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge bg-[#2AA107] badge-sm text-white font-bold border-0 shrink-0 whitespace-nowrap px-2.5 py-1">
                          {currentStep > 3 ? '✓ Step 3' : 'Step 3'}
                        </span>
                        {currentStep > 3 && <span className="text-xs text-[#2AA107] font-bold shrink-0">Edit ✎</span>}
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Health Pre-Approval</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs text-base-content/85">
                          <p><strong>3 Health Questions:</strong> {currentStep > 3 ? 'Completed ✓' : 'Instant NO / YES'}</p>
                          <p className="text-[11px] text-base-content/70">No Medical Exam Required</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline border-[#2AA107] text-[#2AA107] hover:bg-[#2AA107] hover:text-white btn-xs font-bold w-full rounded-xl">
                          {currentStep > 3 ? 'Edit Answers ✎' : 'Answer 3 Questions →'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 4: DIRECT DEPOSIT & SIGNATURE CARD --- */}
                <div
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 4
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 3
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[30%] sm:-translate-x-[30%] md:-translate-x-[32%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep < 4) handleProceedToStep4();
                  }}
                >
                  {currentStep === 4 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between gap-2 border-b border-base-200 pb-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0 shadow-xs shrink-0 whitespace-nowrap px-2.5 py-1">
                            Step 4
                          </span>
                          <h2 className="text-sm sm:text-base font-bold text-base-content leading-snug truncate sm:whitespace-normal">
                            Direct Deposit (ACH) and Signature
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium shrink-0 whitespace-nowrap">4 of 4</span>
                      </div>

                      {submissionError && (
                        <div className="alert alert-error text-xs text-white p-3 rounded-xl flex items-center gap-2 shadow-md">
                          <FaExclamationCircle className="shrink-0" />
                          <span>{submissionError}</span>
                        </div>
                      )}

                      <form onSubmit={handleFinalSubmit} className="space-y-4">
                        {/* 1. Routing Number */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="label py-0">
                              <span className="label-text font-semibold text-xs text-base-content">
                                Routing number: <span className="text-error">*</span>
                              </span>
                            </label>
                            {achPayment.routingNumber.length === 9 && (
                              <span className="text-[10px] font-bold text-[#2AA107] inline-flex items-center gap-0.5">
                                <FaCheck className="text-[9px]" /> Valid 9 Digits
                              </span>
                            )}
                          </div>
                          <input
                            type="text"
                            maxLength={9}
                            placeholder="9-digit routing number"
                            className={`input input-bordered input-sm w-full text-xs font-mono font-bold focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                              achErrors.routingNumber ? 'input-error' : ''
                            }`}
                            value={achPayment.routingNumber}
                            onChange={(e) => {
                              setAchPayment((prev) => ({ ...prev, routingNumber: e.target.value.replace(/\D/g, '') }));
                              if (achErrors.routingNumber) setAchErrors((prev) => ({ ...prev, routingNumber: '' }));
                            }}
                          />
                          {achErrors.routingNumber && <span className="text-xs text-error mt-1 block">{achErrors.routingNumber}</span>}
                        </div>

                        {/* 2. Bank Name */}
                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Bank name: <span className="text-error">*</span>
                            </span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Chase, Bank of America, Wells Fargo"
                            className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                              achErrors.bankName ? 'input-error' : ''
                            }`}
                            value={achPayment.bankName}
                            onChange={(e) => {
                              setAchPayment((prev) => ({ ...prev, bankName: e.target.value }));
                              if (achErrors.bankName) setAchErrors((prev) => ({ ...prev, bankName: '' }));
                            }}
                          />
                          {achErrors.bankName && <span className="text-xs text-error mt-1 block">{achErrors.bankName}</span>}
                        </div>

                        {/* 3. Account Number */}
                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Account number: <span className="text-error">*</span>
                            </span>
                          </label>
                          <input
                            type="text"
                            placeholder="Bank account number"
                            className={`input input-bordered input-sm w-full text-xs font-mono font-bold focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                              achErrors.accountNumber ? 'input-error' : ''
                            }`}
                            value={achPayment.accountNumber}
                            onChange={(e) => {
                              setAchPayment((prev) => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '') }));
                              if (achErrors.accountNumber) setAchErrors((prev) => ({ ...prev, accountNumber: '' }));
                            }}
                          />
                          {achErrors.accountNumber && <span className="text-xs text-error mt-1 block">{achErrors.accountNumber}</span>}
                        </div>

                        {/* 4. Name Appearing on Bank Details */}
                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Name appearing on bank details: <span className="text-error">*</span>
                            </span>
                          </label>
                          <input
                            type="text"
                            placeholder="Full name as it appears on your bank statement"
                            className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none rounded-xl ${
                              achErrors.bankCustomerName ? 'input-error' : ''
                            }`}
                            value={achPayment.bankCustomerName}
                            onChange={(e) => {
                              setAchPayment((prev) => ({ ...prev, bankCustomerName: e.target.value }));
                              if (achErrors.bankCustomerName) setAchErrors((prev) => ({ ...prev, bankCustomerName: '' }));
                            }}
                          />
                          {achErrors.bankCustomerName && <span className="text-xs text-error mt-1 block">{achErrors.bankCustomerName}</span>}
                        </div>

                        {/* Electronic Signature */}
                        <div className="pt-3 border-t border-base-200 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <label className="label py-0">
                              <span className="label-text font-semibold text-xs text-base-content flex items-center gap-1.5">
                                <FaFileSignature className="text-[#2AA107]" /> Electronic Signature <span className="text-error">*</span>
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => sigCanvasRef.current?.clear()}
                              className="btn btn-ghost btn-xs text-[11px] text-base-content/70 hover:text-error cursor-pointer"
                            >
                              <CiEraser className="text-sm" /> Clear
                            </button>
                          </div>

                          <div className="border border-base-300 rounded-2xl bg-base-100 p-2.5 text-center shadow-inner relative">
                            <ReactSignatureCanvas
                              ref={sigCanvasRef}
                              penColor="#0f172a"
                              velocityFilterWeight={0.7}
                              canvasProps={{ className: 'signature-canvas w-full h-36 sm:h-44 bg-base-100 rounded-xl cursor-crosshair' }}
                            />
                            <div className="flex justify-start items-center pt-2 border-t border-base-200">
                              <span className="text-[10px] text-base-content/50 font-medium">
                                ✍️ Sign with finger or mouse above
                              </span>
                            </div>
                          </div>
                          {achErrors.signature && <span className="text-xs text-error mt-1 block">{achErrors.signature}</span>}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-2">
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={handleBackToStep3}
                            className="btn btn-outline border-base-300 text-base-content/80 hover:bg-base-200 hover:text-base-content w-1/3 text-xs font-bold rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                          >
                            <FaArrowLeft className="text-[10px]" />
                            <span>Back</span>
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn flex-1 btn-secondary text-white font-black uppercase text-xs tracking-wider shadow-md rounded-xl h-11 min-h-[44px] flex items-center justify-center gap-2 hover:scale-[1.01] active:scale-[0.99] transition-all cursor-pointer"
                          >
                            {isSubmitting ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="loading loading-spinner loading-xs"></span>
                                <span>Processing...</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <FaLock className="text-xs" />
                                <span>SUBMIT APPLICATION and PROCESS PAYMENT</span>
                              </span>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    /* Peeking Summary for Step 4 */
                    <div 
                      onClick={handleProceedToStep4}
                      className="flex flex-col justify-between h-full space-y-4 py-1 text-left cursor-pointer"
                    >
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge bg-[#2AA107] badge-sm text-white font-bold border-0">Step 4</span>
                        <FaLock className="text-xs text-base-content/50" />
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Direct Deposit and Sign</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs text-base-content/85">
                          <p><strong>Payment and Signature:</strong> Secure ACH Debit</p>
                          <p className="text-[11px] text-base-content/70">Complete previous step to sign</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline border-[#2AA107] text-[#2AA107] hover:bg-[#2AA107] hover:text-white btn-xs font-bold w-full rounded-xl">
                          Proceed to Payment and Sign →
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 5: CONFIRMATION CARD --- */}
                {currentStep === 5 && (
                  <div className="w-full max-w-xl sm:max-w-2xl mx-auto rounded-2xl sm:rounded-3xl border border-base-300 bg-base-100 shadow-xl p-6 sm:p-8 space-y-5 text-center animate-fadeIn">
                    <div className="w-14 h-14 rounded-full bg-[#2AA107]/10 text-[#2AA107] flex items-center justify-center mx-auto shadow-inner">
                      <FaCheckCircle className="text-3xl" />
                    </div>

                    <div className="space-y-1">
                      <h2 className="text-xl sm:text-2xl font-black text-base-content tracking-tight">
                        Application Submitted Successfully!
                      </h2>
                    </div>

                    <div className="bg-base-200/60 border border-base-200 rounded-2xl p-4 sm:p-5 text-left text-xs sm:text-sm space-y-3">
                      <div className="flex justify-between border-b border-base-200 pb-2.5">
                        <span className="text-base-content/70">Application Reference ID:</span>
                        <strong className="text-base-content font-mono font-bold">#{applicationRefId}</strong>
                      </div>
                      <div className="flex justify-between border-b border-base-200 pb-2.5">
                        <span className="text-base-content/70">Selected Plan:</span>
                        <strong className="text-base-content font-bold">${rateCalculation.coverageAmount.toLocaleString()} Lump Sum Benefits</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Monthly Premium Quote:</span>
                        <strong className="text-[#2AA107] font-black text-sm sm:text-base">${rateCalculation.totalMonthlyPremium.toFixed(2)}/month</strong>
                      </div>
                    </div>

                    <div className="bg-base-200/40 border border-base-200 rounded-2xl p-4 text-xs sm:text-sm text-base-content/80 text-center leading-relaxed">
                      Our Underwriting Department is reviewing your file. If more information is needed, you will be contacted.
                    </div>

                    <div className="pt-2 flex justify-center">
                      <button
                        type="button"
                        onClick={handleStartNewQuote}
                        className="btn btn-ghost btn-sm text-xs font-bold text-[#2AA107] rounded-xl cursor-pointer hover:bg-base-200"
                      >
                        <FaRedo className="text-xs" /> Start New Quote
                      </button>
                    </div>
                  </div>
                )}

              </div>

            </div>

            {/* ================= REAL-WORLD SCENARIOS SECTION ================= */}
            <div id="how-it-works-scenarios" className="pt-6 sm:pt-10 space-y-6">
              <div className="text-center space-y-2 max-w-2xl mx-auto px-2">
                <span className="badge bg-[#2AA107]/15 text-[#2AA107] font-bold text-xs px-3 py-2 border-0">
                  Real-World Claim Scenarios
                </span>
                <h2 className="text-xl sm:text-3xl font-black text-base-content tracking-tight">
                  How Lump Sum Benefits Work in Real Life
                </h2>
                <p className="text-xs sm:text-sm text-base-content/75 leading-relaxed">
                  Health insurance pays hospitals for medical procedures. A Lump Sum policy deposits cash directly into your personal checking account so you can pay bills, mortgages, and living expenses during recovery.
                </p>
              </div>

              {/* Scenario Toggle Buttons */}
              <div className="flex justify-center px-2">
                <div className="bg-base-200/80 p-1.5 rounded-2xl flex flex-wrap sm:flex-nowrap gap-1.5 max-w-lg w-full">
                  <button
                    type="button"
                    onClick={() => setActiveScenario('mary')}
                    className={`flex-1 py-2 px-3 sm:px-4 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${
                      activeScenario === 'mary'
                        ? 'bg-white text-base-content shadow-sm border border-base-200'
                        : 'text-base-content/70 hover:text-base-content hover:bg-white/50'
                    }`}
                  >
                    Scenario 1: Cancer Protection
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveScenario('john')}
                    className={`flex-1 py-2 px-3 sm:px-4 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${
                      activeScenario === 'john'
                        ? 'bg-white text-base-content shadow-sm border border-base-200'
                        : 'text-base-content/70 hover:text-base-content hover:bg-white/50'
                    }`}
                  >
                    Scenario 2: Heart Attack & Business
                  </button>
                </div>
              </div>

              {/* Scenario Visual Cards */}
              <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-2">
                {activeScenario === 'mary' ? (
                  <>
                    <div 
                      onClick={() => setLightboxImage(ls1)}
                      className="bg-white border border-base-200 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                    >
                      <div className="overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center">
                        <img
                          src={ls1}
                          alt="Scenario 1: Mary Cancer Protection Case Study"
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="pt-3 text-center text-[11px] text-base-content/60 font-medium">
                        Click image to view
                      </div>
                    </div>

                    <div 
                      onClick={() => setLightboxImage(ls2)}
                      className="bg-white border border-base-200 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                    >
                      <div className="overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center">
                        <img
                          src={ls2}
                          alt="Scenario 1: Mary Claim Timeline and Payout"
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="pt-3 text-center text-[11px] text-base-content/60 font-medium">
                        Click image to view
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div 
                      onClick={() => setLightboxImage(ls3)}
                      className="bg-white border border-base-200 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                    >
                      <div className="overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center">
                        <img
                          src={ls3}
                          alt="Scenario 2: John Heart Attack Protection Case Study"
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="pt-3 text-center text-[11px] text-base-content/60 font-medium">
                        Click image to view
                      </div>
                    </div>

                    <div 
                      onClick={() => setLightboxImage(ls4)}
                      className="bg-white border border-base-200 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                    >
                      <div className="overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center">
                        <img
                          src={ls4}
                          alt="Scenario 2: John Claim Timeline and Payout"
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="pt-3 text-center text-[11px] text-base-content/60 font-medium">
                        Click image to view
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Back to Calculator CTA */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn btn-secondary text-white font-black px-8 text-xs tracking-wider rounded-xl uppercase shadow-md cursor-pointer hover:shadow-lg inline-flex items-center gap-2.5 hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  <span>Calculate Your Estimated Rate</span>
                  <FaArrowCircleUp className="text-base shrink-0" />
                </button>
              </div>
            </div>

          </div>
        }
      />

      {/* Unified 3-Tab Legal / TCPA Modal */}
      <Modal
        open={showLegalModal}
        handleClose={() => setShowLegalModal(false)}
        closeButton={false}
        disableBackgroundClose={true}
        maxWidth="sm:max-w-2xl"
      >
        <div className="p-4 sm:p-6 space-y-3.5 text-left w-full">
          <div className="flex border-b border-base-200 gap-1 overflow-x-auto">
            <button
              type="button"
              onClick={() => setLegalTab('terms')}
              className={`py-1.5 px-3 text-xs font-bold rounded-t-lg border-b-2 transition-colors cursor-pointer whitespace-nowrap ${legalTab === 'terms' ? 'border-primary text-primary bg-primary/10' : 'text-base-content/60 hover:text-base-content'}`}
            >
              Terms and Conditions
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('privacy')}
              className={`py-1.5 px-3 text-xs font-bold rounded-t-lg border-b-2 transition-colors cursor-pointer whitespace-nowrap ${legalTab === 'privacy' ? 'border-primary text-primary bg-primary/10' : 'text-base-content/60 hover:text-base-content'}`}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('comm')}
              className={`py-1.5 px-3 text-xs font-bold rounded-t-lg border-b-2 transition-colors cursor-pointer whitespace-nowrap ${legalTab === 'comm' ? 'border-primary text-primary bg-primary/10' : 'text-base-content/60 hover:text-base-content'}`}
            >
              Communication Agreement
            </button>
          </div>

          <div className="modal-legal-content max-h-[320px] overflow-y-auto text-xs text-base-content/80 leading-relaxed bg-base-200/50 p-3 rounded-xl border border-base-200">
            {legalTab === 'terms' && <TermsAndConditionContent />}
            {legalTab === 'privacy' && <PrivacyPolicyContent />}
            {legalTab === 'comm' && (
              <div className="space-y-3.5 text-xs text-base-content/85">
                <div className="border-b border-base-200 pb-1.5">
                  <p className="text-[11px] text-base-content/60">
                    Effective Date: August 4, 2026 • insuranceenterpriseusa.com
                  </p>
                </div>

                <p>
                  By submitting your contact information (including your phone number and email address) through any online quote form, health plan calculator, enrollment form, or inquiry portal on this Site, you provide your express written consent and agreement to the following terms:
                </p>

                <div className="space-y-3">
                  <div>
                    <h5 className="font-bold text-base-content">1. Express Written Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You authorize Insurance Enterprise USA, its licensed agents, affiliates, and representatives to contact you regarding health insurance quotes, plan options, coverage details, and related insurance services via telephone calls, SMS/MMS text messages, and email at the phone number and email address provided.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">2. Automated Technology</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You understand and agree that communications may be transmitted using automated telephone dialing systems (ATDS), artificial or prerecorded voice messages, automated SMS systems, and computer-generated emails.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">3. Consent Not Required for Purchase</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Your consent to receive promotional and informational communications is completely voluntary and is <strong>not a condition of purchasing</strong> any goods, services, or insurance policies from Insurance Enterprise USA.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">4. Message Frequency and Carrier Rates</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Message frequency varies depending on your inquiries and active requests. Standard message and data rates charged by your mobile wireless carrier may apply to all SMS/MMS messages.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">5. Opt-Out and Revocation of Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You may revoke your consent or opt out at any time. To stop receiving text messages, reply <strong>STOP</strong> to any SMS received. To request help, reply <strong>HELP</strong>. To unsubscribe from emails, click the unsubscribe link at the bottom of any email. You may also contact our compliance department at <span className="font-bold text-primary">888-350-6605</span> or via email at <span className="font-bold text-primary">enroll@insuranceenterpriseusa.com</span>.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">6. E-SIGN Disclosure</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You consent to receive all notices, policy documents, disclosures, and communications electronically pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-base-200 flex items-center justify-end gap-2.5">
            <button
              type="button"
              disabled={isCalculating}
              onClick={() => setShowLegalModal(false)}
              className="btn btn-outline border-base-300 text-base-content/70 hover:bg-base-200 text-xs font-bold rounded-xl h-10 min-h-[40px] px-5 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isCalculating}
              onClick={handleModalAgree}
              className="btn btn-secondary text-white font-bold px-8 text-xs tracking-wider rounded-xl h-10 min-h-[40px] uppercase shadow-md cursor-pointer disabled:opacity-60"
            >
              I Agree &amp; Confirm
            </button>
          </div>
        </div>
      </Modal>

      {/* Fullscreen Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[99999] bg-black/30 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 md:p-6 animate-fadeIn cursor-pointer select-none"
          onClick={() => setLightboxImage(null)}
        >
          {/* Close button at top right */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImage(null);
            }}
            className="absolute top-3 right-3 sm:top-5 sm:right-5 btn btn-circle btn-sm sm:btn-md bg-black/70 hover:bg-black text-white border-none transition-all shadow-2xl z-50 cursor-pointer flex items-center justify-center"
            title="Close Fullscreen View"
            aria-label="Close"
          >
            <FaTimes className="text-base sm:text-lg" />
          </button>

          {/* Fullscreen Image */}
          <div
            className="relative flex items-center justify-center w-full h-full max-w-[100vw] max-h-[100vh]"
            onClick={() => setLightboxImage(null)}
          >
            <img
              src={lightboxImage}
              alt="Plan Detail Illustration (Fullscreen)"
              className="max-w-[96vw] max-h-[94vh] w-auto h-auto object-contain rounded-xl shadow-2xl ring-1 ring-black/15 pointer-events-auto bg-white cursor-default"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Dynamic Toast Feedback System */}
      {toast.show && (
        <div className="toast toast-top toast-center z-[9999] animate-fadeIn w-full max-w-sm sm:max-w-md px-4 sm:px-0 pointer-events-none">
          <div
            ref={toastBoxRef}
            className={`alert shadow-2xl rounded-2xl flex items-start sm:items-center justify-between gap-2.5 py-3 px-4 border-0 text-xs font-semibold w-full pointer-events-auto transition-all ${
              toast.type === 'success'
                ? 'bg-[#2AA107] text-white'
                : toast.type === 'error'
                ? 'alert-error text-white'
                : toast.type === 'warning'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-800 text-white'
            }`}
          >
            <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
              {toast.type === 'success' && <FaCheckCircle className="text-sm shrink-0 mt-0.5 sm:mt-0 text-white" />}
              {toast.type === 'error' && <FaExclamationCircle className="text-sm shrink-0 mt-0.5 sm:mt-0 text-white" />}
              {toast.type === 'warning' && <FaExclamationTriangle className="text-sm shrink-0 mt-0.5 sm:mt-0 text-white" />}
              {toast.type === 'info' && <FaInfoCircle className="text-sm shrink-0 mt-0.5 sm:mt-0 text-white" />}
              <span className="leading-snug break-words whitespace-normal min-w-0 flex-1 text-left">
                {toast.message}
              </span>
            </div>
            <button
              type="button"
              onClick={hideToast}
              className="hover:opacity-75 transition-opacity p-1 rounded-full hover:bg-black/15 shrink-0 cursor-pointer ml-1 self-start sm:self-center"
              aria-label="Dismiss notification"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Off-screen Document for High-Resolution Application PDF Generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '816px', zIndex: -1000, pointerEvents: 'none', overflow: 'hidden' }}>
        <LumpSumApplicationDoc
          docRef={applicationDocRef}
          formData={formData}
          rateCalculation={rateCalculation}
          questionnaire={questionnaire}
          achPayment={achPayment}
          applicationRefId={applicationRefId}
          submissionDate={formatDateTime()}
          ipAddress={ipAddress}
          signatureImage={
            docSignatureImage ||
            (achPayment.signatureType === 'draw' && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()
              ? sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png')
              : achPayment.typedSignature)
          }
          achDisclaimer={ACH_DISCLAIMER_TEXT}
          tcpaDisclaimer={TCPA_CALCULATOR_DISCLAIMER}
        />
      </div>
    </div>
  );
};

export default LumpSumBenefits;
